import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { useConfiguration } from '../../context/configurationContext';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { REVIEW_TYPE_OF_CUSTOMER, REVIEW_TYPE_OF_PROVIDER } from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';

import {
  ButtonTabNavHorizontal,
  H4,
  Heading,
  IconBrandCard,
  LayoutSideNavigation,
  ListingCard,
  NamedRedirect,
  Page,
  Reviews,
  Modal,
} from '../../components';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';

import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import Map, { Marker } from 'react-map-gl';
import css from './ProfilePage.module.css';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import Slider from 'react-slick';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import {
  calculateGeographicalMidpoint,
  extractCountryCodeFromAddress,
  getCountryFlagImage,
} from '../../util/data';
import { createResourceLocatorString } from '../../util/routes';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import EndorsementsCard from './Cards/EndorsementsCard/EndorsementsCard';
import ReviewCard from './Cards/ReviewCard/ReviewCard';
import WisdomCard from './Cards/WisdomCard/WisdomCard';
import {
  queryAddUserToTribe,
  searchProfileListingSuggestions,
  sendSuccessJoinToTribeEmail,
  showUser,
} from './ProfilePage.duck';
import useDebounce from '../../hooks/useDebounce';
import CredentialCard from './Cards/CredentialCard/CredentialCard';
import EndorseModal from './EndorseModal/EndorseModal';
import AsideContent from './AsideContent/AsideContent';

// const MAX_MOBILE_SCREEN_WIDTH = 768;
// const MIN_LENGTH_FOR_LONG_WORDS = 20;

const joinWithAnd = strings => {
  if (!Array.isArray(strings)) return '';

  const len = strings.length;

  if (len === 0) return '';
  if (len === 1) return strings[0];
  if (len === 2) return `${strings[0]} and ${strings[1]}`;

  return `${strings.slice(0, -1).join(', ')} and ${strings[len - 1]}`.replaceAll('_', ' ');
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  return (
    <div className={css.mobileReviews}>
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromMyCustomersTitle"
          values={{ count: reviewsOfProvider.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfProvider} />
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsAsACustomerTitle"
          values={{ count: reviewsOfCustomer.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfCustomer} />
    </div>
  );
};

export const DesktopReviews = props => {
  const [showReviewsType, setShowReviewsType] = useState(REVIEW_TYPE_OF_PROVIDER);
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;
  const desktopReviewTabs = [
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsFromMyCustomersTitle"
            values={{ count: reviewsOfProvider.length }}
          />
        </Heading>
      ),
      selected: isReviewTypeProviderSelected,
      onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
    },
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsAsACustomerTitle"
            values={{ count: reviewsOfCustomer.length }}
          />
        </Heading>
      ),
      selected: isReviewTypeCustomerSelected,
      onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
    },
  ];

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal className={css.desktopReviewsTabNav} tabs={desktopReviewTabs} />
        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
        {isReviewTypeProviderSelected ? (
          <Reviews reviews={reviewsOfProvider} />
        ) : (
          <Reviews reviews={reviewsOfCustomer} />
        )}
      </div>
    </div>
  );
};

export const MainContent = props => {
  const history = useHistory();
  const [keyword, setKeyword] = useState('');
  const debouncedKeyword = useDebounce(keyword, 300);
  const [modal, setModal] = useState(null);

  const {
    userShowError,
    bio,
    listings,
    queryListingsError,
    reviews = [],
    publicData,
    endorsedUsers,
    routeConfiguration,
    tribeServicesListings,
    onUpdateProfile = () => {},
    onQueryAddUserToTribe = () => {},
    onShowUser,
    onSendSuccessJoinToTribeEmail,
    currentUser = {},
    isCurrentUser = false,
    config,
    posts,
    onSearchProfileListingSuggestions,
    suggestions,
    isCustomer,
    onManageDisableScrolling,
    intl,
  } = props;

  useEffect(() => {
    onSearchProfileListingSuggestions(debouncedKeyword, config);
  }, [debouncedKeyword, config]);

  const openModalHandler = senderId => {
    setModal(senderId);
  };
  const closeModalHandler = () => {
    setModal(null);
  };

  // Posts are now fetched directly for the author slug, so no need to filter
  const filteredPosts = posts?.data || [];

  const [showRequests, setShowRequests] = useState(true);
  const [viewport, setViewport] = useState({
    longitude: -74.5,
    latitude: 40,
    zoom: 1,
  });

  useEffect(() => {
    if (endorsedUsers?.length > 0) {
      const locations = endorsedUsers
        .map(user => user?.attributes?.profile?.publicData?.location?.origin)
        .filter(loc => loc?.lat && loc?.lng);

      if (locations.length > 0) {
        const { lat, lng } = calculateGeographicalMidpoint(locations);
        setViewport(prev => ({
          ...prev,
          longitude: lng,
          latitude: lat,
        }));
      }
    }
  }, [endorsedUsers]);

  const {
    endorsedUsersInfo = [],
    endoresementRequest = [],
    endorsedUsersId = [],
    sacredPractice,
    certifications,
    awardsArray,
    licenses,
    lineages,
    memberships,
    teachings,
  } = publicData ?? {};

  const hasBio = !!bio;

  const formattedContent = sacredPractice ?
    sacredPractice
    .split(/\n{2,}/)
    .map((block, index) => {
      const lines = block.split('\n');
      const isBlockList = lines.every(line => line.trim().startsWith('•'));

      if (isBlockList) {
        return (
          <ul key={index} style={{ paddingLeft: '1.2em', marginBottom: '1em' }}>
            {lines.map((line, i) => (
              <li key={i}>{line.replace(/^•\s*/, '')}</li>
            ))}
          </ul>
        );
      }

      // Обрабатываем inline-список в параграфе
      if (block.includes('•')) {
        const parts = block.split(/•\s*/).filter(Boolean);
        return (
          <p key={index} style={{ marginBottom: '1em' }}>
            {parts[0]}
            <ul style={{ paddingLeft: '1.2em', marginTop: '0.5em' }}>
              {parts.slice(1).map((item, i) => (
                <li key={i}>{item.trim()}</li>
              ))}
            </ul>
          </p>
        );
      }

      return <p key={index} style={{ marginBottom: '1em' }}>{block}</p>;
    })
  : '';

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }

  const reviewsTabLabel = isCurrentUser ? 'My Reviews' : 'Client Reviews';

  const tabs = [
    { label: 'My Practice' },
    { label: 'My Tribe & Endorsements' },
    { label: reviewsTabLabel },
    { label: 'Credentials & Legacy' },
  ];
  // const visibleTabs = isCurrentUser ? tabs.filter(tab => tab.label === 'My Reviews') : tabs;
  const visibleTabs = isCustomer ? tabs.filter(tab => tab.label === 'Client Reviews') : tabs;
  const [activeTab, setActiveTab] = useState(0);

  function SampleNextArrow(props) {
    const { className, onClick } = props;
    return (
      <div className={className} onClick={onClick}>
        <IconBrandCard type="arrowright" />
      </div>
    );
  }

  function SamplePrevArrow(props) {
    const { className, onClick } = props;
    return (
      <div className={className} onClick={onClick}>
        <IconBrandCard type="arrowleft" />
      </div>
    );
  }

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 3,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2.3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          initialSlide: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };
  const tribeServicesSetting = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 3,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2.3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          initialSlide: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const declineRequest = id => {
    const profileToAccept = endoresementRequest?.filter(data => data?.senderId !== id);
    const updatedEndorsedUsersInfo = endorsedUsersInfo.filter(({ userId }) => userId !== id);
    const profile = {
      publicData: {
        endoresementRequest: profileToAccept,
        endorsedUsersInfo: updatedEndorsedUsersInfo,
      },
    };
    onQueryAddUserToTribe({
      receiverId: id,
      senderId: currentUser?.id?.uuid,
      action: 'decline',
      config: config,
    });
    onUpdateProfile(profile);
  };

  const handleSubmitEndorsement = values => {
    const id = modal;
    const text = values?.yourEndorsment;
    const profileToAccept = endoresementRequest?.filter(data => data?.senderId !== id);
    const endoresement = endoresementRequest.find(data => data?.senderId === id);
    const profile = {
      publicData: {
        endoresementRequest: profileToAccept,
        endorsedUsersId: endorsedUsersId?.includes(id)
          ? endorsedUsersId?.filter(userId => userId !== id)
          : [...endorsedUsersId, id],
        endorsedUsersInfo: endorsedUsersId?.includes(id)
          ? endorsedUsersInfo.filter(({ userId }) => userId !== id)
          : [...endorsedUsersInfo, { userId: id, info: endoresement?.endorsementRequestText }],
      },
    };

    onQueryAddUserToTribe({
      receiverId: id,
      senderId: currentUser?.id?.uuid,
      action: 'accept',
      config: config,
      endorsement: text,
    })
      .then(() => onUpdateProfile(profile))
      .then(() => onShowUser(currentUser?.id, config))
      .then(() => {
        const params = {
          senderId: id,
          receiverId: currentUser?.id?.uuid,
        };
        onSendSuccessJoinToTribeEmail(params);
      })
      .finally(closeModalHandler);
  };

  const draftId = '00000000-0000-0000-0000-000000000000';
  const draftSlug = 'draft';
  const type = 'new';
  const tab = 'details';

  return (
    <div>
      {!isCustomer && (
        <div className={listingsContainerClasses}>
          <H4 as="h2" className={css.listingsTitle}>
            <FormattedMessage id="ProfilePage.listingsTitle" values={{ count: listings.length }} />
          </H4>
          {listings?.length > 0 ? (
            <ul className={css.listings}>
              {listings.map(l => (
                <li className={css.listing} key={l.id.uuid}>
                  <ListingCard listing={l} showAuthorInfo={false} />
                </li>
              ))}
            </ul>
          ) : (
            <>
              {isCurrentUser ? (
                <p
                  onClick={() => {
                    history.push(
                      createResourceLocatorString(
                        'EditListingPage',
                        routeConfiguration,
                        {
                          slug: draftSlug,
                          id: draftId,
                          type,
                          tab,
                        },
                        {}
                      )
                    );
                  }}
                  className={css.listService}
                >
                  List your first service here
                </p>
              ) : (
                <p>Services not found</p>
              )}
            </>
          )}
        </div>
      )}

      <div className={css.profileTabs}>
        <div className={css.tabHead}>
          {visibleTabs.map((tab, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              className={`${css.tabButton} ${activeTab === index ? css.activeTab : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className={css.tabContent}>
          {visibleTabs[activeTab]?.label === 'My Practice' && (
            <div className={css.tabPanel}>
              <div className={css.tabBody}>
                {sacredPractice?.length > 0 ? (
                  <div className={css.aboutPracticeScript}>
                    {formattedContent}
                  </div>
                ) : (
                  <p>No information about my practice have been received yet</p>
                )}

                {/* Collective Wisdom */}
                {filteredPosts?.length > 0 ? (
                  <div className={css.endorsementsBox}>
                    <div className={css.tabTitle}>Collective Wisdom</div>
                    <div className={css.wisdomBox}>
                    <Slider {...settings}>
                        {filteredPosts.map((item, i) => {
                          const countryName = extractCountryCodeFromAddress(
                            currentUser?.attributes?.profile?.publicData?.location?.address
                          );
                          const abbreviatedName = currentUser?.attributes?.profile?.abbreviatedName;

                          return (
                            <a key={i} href={item?.url} target="_blank" rel="noopener noreferrer">
                              <WisdomCard
                                wisdomImage={item?.feature_image}
                                wisdomDate={item.created_at}
                                wisdomTitle={item.title}
                                wisdomDescription={item.html}
                                wisdomProfile={item?.primary_author?.profile_image}
                                name={item?.primary_author?.name}
                                locationFlag={getCountryFlagImage(countryName)}
                                locationName={
                                  currentUser?.attributes?.profile?.publicData?.location?.address
                                }
                                abbreviatedName={abbreviatedName}
                              />
                            </a>
                          );
                        })}
                      </Slider>
                    </div>
                  </div>
                ) : (
                  isCurrentUser && (
                    <div className={css.endorsementsBox}>
                      <div className={css.tabTitle}>Collective Wisdom</div>
                      <p>
                        This is where the blog content contributions from your tribe members will
                        show.
                      </p>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {visibleTabs[activeTab]?.label === 'My Tribe & Endorsements' && (
            <div className={css.tabPanel}>
              <div className={css.tabBody}>
                {/* Requests */}
                <div className={css.headingBox}>
                  <div>
                    {isCurrentUser && endoresementRequest.length > 0 && (
                      <h2 className={css.tabTitleRequest}>
                        Requests ({endoresementRequest?.length})
                      </h2>
                    )}
                    {isCurrentUser && endoresementRequest?.length > 0 && (
                      <p className={css.tabSubTitle}>
                        When you endorse back, both of you will join each other's tribe
                      </p>
                    )}
                  </div>
                  {isCurrentUser && endoresementRequest?.length > 0 && (
                    <div className={css.actionArrow}>
                      {!showRequests ? (
                        <div onClick={() => setShowRequests(true)}>
                          <IconBrandCard type="bottomarrow" />
                        </div>
                      ) : (
                        <div onClick={() => setShowRequests(false)}>
                          <IconBrandCard type="toparrow" />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Request cards */}
                {showRequests && isCurrentUser && (
                  <div className={css.requestCardList}>
                    {endoresementRequest?.map(data => {
                      const countryCode = extractCountryCodeFromAddress(data?.address);
                      return (
                        <div className={css.requestCard} key={data?.senderId}>
                          <div className={css.requestMainRow}>
                            <div>
                              <div className={css.requestLeft}>
                                {data?.profilePhoto ? (
                                  <img src={data?.profilePhoto} className={css.requestImage} />
                                ) : (
                                  <span className={css.requestNameAB}>
                                    {data?.name
                                      ?.split(' ')
                                      .map(word => word[0].toUpperCase())
                                      .join('')}
                                  </span>
                                )}
                                <div className={css.requestDetail}>
                                  <div className={css.requestName}>{data?.name}</div>
                                  {countryCode && (
                                    <div className={css.requestLocation}>
                                      <img src={getCountryFlagImage(countryCode)} />
                                      <span className={css.locationName}>{data?.address}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className={css.endorsementRequestText}>
                                {data?.endorsementRequestText}
                              </div>
                            </div>
                            <div className={css.buttonWrapper}>
                              <button
                                className={css.borderbutton}
                                onClick={() => declineRequest(data?.senderId)}
                              >
                                Decline
                              </button>
                              <button
                                className={css.backgroundButton}
                                onClick={() => openModalHandler(data?.senderId)}
                              >
                                Endorse back
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* My Tribe title */}
                {endorsedUsers?.length > 0 || endoresementRequest?.length > 0 ? (
                  <h2 className={css.tabTitle}>My Tribe ({endorsedUsers?.length})</h2>
                ) : (
                  isCurrentUser && (
                    <p>Your requests will appear here once received. Check back soon!</p>
                  )
                )}

                {/* Search */}
                <div className={css.searchWrapper}>
                  {isCurrentUser && (
                    <div className={css.searchBar}>
                      <div className={css.searchFormGroup}>
                        <span className={css.searchIcon}>
                          <IconBrandCard type="search" />
                        </span>
                        <input
                          type="text"
                          placeholder="Find a tribe member"
                          className={css.inputBox}
                          value={keyword}
                          onChange={e => setKeyword(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              history.push(
                                createResourceLocatorString(
                                  'SearchTribePage',
                                  routeConfiguration,
                                  {},
                                  { keywords: keyword }
                                )
                              );
                            }
                          }}
                        />
                        <span
                          onClick={() =>
                            history.push(
                              createResourceLocatorString(
                                'SearchTribePage',
                                routeConfiguration,
                                {},
                                {}
                              )
                            )
                          }
                          className={css.searchButton}
                        >
                          Search
                        </span>
                      </div>
                    </div>
                  )}
                  {suggestions?.length > 0 && (
                    <div className={css.listingList}>
                      {suggestions.map(data => {
                        const countryCode = extractCountryCodeFromAddress(
                          data?.attributes?.publicData?.location?.address
                        );
                        return (
                          <div
                            onClick={() =>
                              history.push(
                                createResourceLocatorString(
                                  'SearchTribePage',
                                  routeConfiguration,
                                  {},
                                  { id: data?.id?.uuid }
                                )
                              )
                            }
                            key={data?.id?.uuid}
                            className={css.listingCard}
                          >
                            <div className={css.listingLeftSide}>
                              <div className={css.listingProfile}>
                                {data.author.profileImage ? (
                                  <img
                                    src={
                                      data.author.profileImage.attributes.variants['listing-card']
                                        .url
                                    }
                                  />
                                ) : (
                                  <span>{data.author?.attributes?.profile?.abbreviatedName}</span>
                                )}
                              </div>
                              <div className={css.details}>
                                <div className={css.title}>{data?.attributes?.title}</div>
                                <div className={css.location}>
                                  <img src={getCountryFlagImage(countryCode)} />
                                  {data?.attributes?.publicData?.location?.address}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Map */}
                <div className={css.mapBox}>
                  <Map
                    {...viewport}
                    onMove={evt => setViewport(evt.viewState)}
                    style={{ width: '100%', height: '500px', position: 'relative' }}
                    mapStyle="mapbox://styles/mapbox/light-v11"
                    mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
                    projection="mercator"
                    attributionControl={false}
                    onLoad={e => {
                      const map = e.target;
                      map.on('style.load', () => {
                        map.getStyle().layers.forEach(layer => {
                          if (layer.id.includes('place') && !layer.id.includes('country')) {
                            map.setLayoutProperty(layer.id, 'visibility', 'none');
                          }
                        });
                      });
                    }}
                  >
                    {endorsedUsers.length === 0 && !isCurrentUser && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          fontSize: '16px',
                          color: '#333',
                          zIndex: 10,
                        }}
                      >
                        This practitioner hasn’t connected with their tribe yet. Check back soon or
                        explore other practitioners!
                      </div>
                    )}
                    {endorsedUsers?.map(data => {
                      const lat = data?.attributes?.profile?.publicData?.location?.origin?.lat;
                      const lng = data?.attributes?.profile?.publicData?.location?.origin?.lng;
                      const profileImageUrl =
                        data?.profileImage?.attributes?.variants['square-small'].url;
                      const location = data?.attributes?.profile?.publicData?.location?.address;
                      const countryCode = extractCountryCodeFromAddress(location);
                      const abbreviatedName = data?.attributes?.profile?.abbreviatedName;

                      return lat && lng ? (
                        <Marker
                          onClick={() =>
                            history.push(
                              createResourceLocatorString(
                                'ProfilePage',
                                routeConfiguration,
                                { id: data?.id?.uuid },
                                {}
                              )
                            )
                          }
                          longitude={lng}
                          latitude={lat}
                          key={data.id.uuid}
                        >
                          <div className={css.markerBox}>
                            {profileImageUrl ? (
                              <img
                                className={css.markerProfileImage}
                                src={profileImageUrl}
                                alt={data?.attributes?.profile?.displayName}
                              />
                            ) : (
                              <span className={css.abbreviatedName}>{abbreviatedName}</span>
                            )}
                            <div>
                              <img
                                className={css.markerFlagIcon}
                                src={getCountryFlagImage(countryCode)}
                              />
                            </div>
                          </div>
                        </Marker>
                      ) : null;
                    })}
                  </Map>
                </div>

                {/* My Endorsements */}
                {(endorsedUsers?.length > 0 || isCurrentUser) && (
                  <div className={css.endorsementsBox}>
                    <div className={css.tabTitle}>My Endorsements</div>
                    {endorsedUsers?.length > 0 ? (
                      <div className={css.endorsedRow}>
                        {endorsedUsers.map(({ id, attributes, profileImage }, i) => {
                          const {
                            profile: { publicData, abbreviatedName, displayName },
                          } = attributes;

                          const countryCode = extractCountryCodeFromAddress(
                            publicData?.location?.address
                          );

                          return (
                            <EndorsementsCard
                              key={id?.uuid || i}
                              abbreviatedName={abbreviatedName}
                              cardImage={profileImage?.attributes?.variants['square-small']?.url}
                              name={displayName}
                              locationFlag={getCountryFlagImage(countryCode)}
                              locationName={publicData?.location?.address}
                              endorsementName={publicData?.wisdom_ways}
                              description={
                                endorsedUsersInfo.find(({ userId }) => userId === id?.uuid)?.info
                              }
                              history={history}
                              routeConfiguration={routeConfiguration}
                              id={id?.uuid}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      isCurrentUser && (
                        <span>
                          This is where your endorsements will show when a tribe member endorses
                          you.
                        </span>
                      )
                    )}
                  </div>
                )}

                {/* Services from My Tribe */}
                {(tribeServicesListings?.length > 0 || isCurrentUser) && (
                  <div className={css.endorsementsBox}>
                    <div className={css.tabTitle}>Services from My Tribe</div>
                    {tribeServicesListings?.length > 0 ? (
                      <div className={css.wisdomBox}>
                        <Slider {...tribeServicesSetting}>
                          {tribeServicesListings.map(l => (
                            <li className={css.listing} key={l.id.uuid}>
                              <ListingCard listing={l} showAuthorInfo={false} />
                            </li>
                          ))}
                        </Slider>
                      </div>
                    ) : (
                      isCurrentUser && (
                        <span>
                          This is where the services offered by your tribe members will show.
                        </span>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {['Client Reviews', 'My Reviews'].includes(visibleTabs[activeTab]?.label) && (
            <div className={css.tabPanel}>
              {reviews?.length > 0 ? (
                <div className={css.tabBody}>
                  <div className={css.endorsementsBox}>
                    <div className={css.wisdomBox}>
                      <Slider {...settings}>
                        {reviews.map((item, i) => {
                          return (
                            <ReviewCard
                              key={item.id?.uuid || i}
                              abbreviatedName={item?.author?.attributes?.profile?.abbreviatedName}
                              avatar={
                                item.author.profileImage &&
                                item.author.profileImage.attributes.variants['square-small'].url
                              }
                              reviewerName={item?.author?.attributes?.profile?.displayName}
                              reviewDate={moment(item.attributes.createdAt).format('LL')}
                              reviewDescription={item.attributes.content}
                              rating={item?.attributes?.rating}
                            />
                          );
                        })}
                      </Slider>
                    </div>
                  </div>
                </div>
              ) : isCustomer ? (
                <p>You have left no reviews yet</p>
              ) : (
                <p>No reviews have been received yet</p>
              )}
            </div>
          )}

          {visibleTabs[activeTab]?.label === 'Credentials & Legacy' && (
            <div className={css.tabPanel}>
              {/* <h2>Wisdom & Recognitions</h2> */}
              {!certifications &&
                !awardsArray &&
                !licenses &&
                !lineages &&
                !memberships &&
                !teachings && <p>Credentials & Legacy has not been added yet</p>}
              <div className={css.credentialsWrapper}>
                {certifications && certifications.length > 0 && (
                  <CredentialCard title="Certifications" data={certifications} withYear />
                )}
                {awardsArray && awardsArray.length > 0 && (
                  <CredentialCard title="Recognitions & Awards" data={awardsArray} withYear />
                )}
                {licenses && licenses.length > 0 && (
                  <CredentialCard title="Licenses" data={licenses} withYear />
                )}
                {lineages && lineages.length > 0 && (
                  <CredentialCard title="Lineage & Initiations" data={lineages} withYear />
                )}
                {memberships && memberships.length > 0 && (
                  <CredentialCard
                    title="Professional Memberships & Affiliations"
                    data={memberships}
                    withYear
                  />
                )}
                {teachings && teachings.length > 0 && (
                  <CredentialCard title="Teaching & Mentorship" data={teachings} withYear />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={!!modal}
        onClose={closeModalHandler}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <EndorseModal
          intl={intl}
          handleSubmit={handleSubmitEndorsement}
          closeModalHandler={closeModalHandler}
        />
      </Modal>
    </div>
  );
};

export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    endorsedUsers,
    useCurrentUser,
    userShowError,
    user,
    tribeServicesListings,
    reviews,
    posts,
    onSearchProfileListingSuggestions,
    suggestions,
    listings,
    ...rest
  } = props;

  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;
  const userType = user?.attributes?.profile?.publicData?.userType;
  const isCustomer = userType === 'customer';

  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="SearchPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData, metadata, protectedData } =
    profileUser?.attributes?.profile || {};
  const { userFields } = config.user;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;
  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || userShowError != null
    : user != null || userShowError != null;

  const wisdomTags = publicData?.wisdom_ways ?? [];
  const listingsTitle = listings.map(({ attributes }) => attributes.title);

  const schemaTitleVars = {
    name: displayName,
    marketplaceName: config.marketplaceName,
    wisdomTag: wisdomTags[0] ?? '',
    service: listingsTitle[0] ?? '',
  };
  const schemaDescriptionVars = {
    name: displayName,
    marketplaceName: config.marketplaceName,
    wisdomTags: joinWithAnd(wisdomTags),
    services: joinWithAnd(listingsTitle),
  };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);
  const schemaDescription = intl.formatMessage(
    { id: 'ProfilePage.schemaDescription' },
    schemaDescriptionVars
  );

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    return null;
  }
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      description={schemaDescription}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <LayoutSideNavigation
        mainColumnClassName={css.profileContent}
        className={`${css.profileWrapper} ${isCurrentUser ? css.profileCurrentWrapper : ''}`}
        sideNavClassName={css.aside}
        topbar={<TopbarContainer />}
        sideNav={
          <AsideContent
            currentUser={currentUser}
            endorsedUsers={endorsedUsers}
            user={profileUser}
            showLinkToProfileSettingsPage={mounted && isCurrentUser}
            displayName={displayName}
            reviews={reviews}
            routeConfiguration={routeConfiguration}
            isCurrentUser={isCurrentUser}
            isCustomer={isCustomer}
          />
        }
        footer={<FooterContainer />}
      >
        <MainContent
          reviews={reviews}
          bio={bio}
          displayName={displayName}
          userShowError={userShowError}
          publicData={publicData}
          metadata={metadata}
          protectedData={protectedData}
          userFieldConfig={userFields}
          hideReviews={hasNoViewingRightsOnPrivateMarketplace}
          intl={intl}
          routeConfiguration={routeConfiguration}
          endorsedUsers={endorsedUsers}
          tribeServicesListings={tribeServicesListings}
          currentUser={profileUser}
          config={config}
          posts={posts}
          onSearchProfileListingSuggestions={onSearchProfileListingSuggestions}
          suggestions={suggestions}
          listings={listings}
          isCustomer={isCustomer}
          {...rest}
        />
      </LayoutSideNavigation>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews = [],
    queryReviewsError,
    listings = [],
    endorsedUsers = [],
    posts = [],
    suggestions = [],
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    reviews,
    queryReviewsError,
    endorsedUsers,
    tribeServicesListings: listings,
    isCurrentUser,
    posts,
    suggestions,
  };
};

const mapDispatchToProps = dispatch => ({
  onQueryAddUserToTribe: params => dispatch(queryAddUserToTribe(params)),
  onUpdateProfile: data => dispatch(updateProfile(data)),
  onSearchProfileListingSuggestions: (keyword, config) =>
    dispatch(searchProfileListingSuggestions(keyword, config)),
  onSendSuccessJoinToTribeEmail: params => dispatch(sendSuccessJoinToTribeEmail(params)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onShowUser: (userId, config) => dispatch(showUser(userId, config)),
});

const ProfilePage = compose(connect(mapStateToProps, mapDispatchToProps))(ProfilePageComponent);

export default ProfilePage;
